import { ChangeEvent, useEffect, useMemo, useState } from "react";
import StatusBadge from "./StatusBadge";
import StageOutOfScopeMessage from "./StageOutOfScopeMessage";
import IntegrationCredentialSections, {
  CredentialFormFields,
} from "./IntegrationCredentialSections";
import {
  AUTH_TYPE,
  AuthTypeId,
  ConnectionTestResult,
  DEFAULT_AUTH_TYPE,
  HttpMethodId,
  STAGE_DESCRIPTIONS,
  STAGE_META,
  ServiceProvider,
  StageConfig,
} from "../../../utils/integrationConfigData";
import {
  AuthTypeApi,
  ConfigurationTypeApi,
  mapAuthTypeIdToApiId,
  mapIntegrationToStageConfig,
} from "../types/providerIntegrationtypes";
import { isTestSuccess } from "../../../constants/integrationKeys";
import {
  formatTestResultMessage,
  saveStageConfig,
} from "../../../utils/integrationApiHelpers";
import { useAuditUserId } from "../../../hooks/useAuditUserId";
import { useStageConfigLoader } from "../../../utils/useStageConfigLoader";
import { KeyValueItem } from "../../../utils/keyValueItems";
import { ProviderIntegrationApi } from "../../../service/Api/ProviderIntegrationApi";
interface StageDtrFormProps {
  provider: ServiceProvider;
  stage?: StageConfig;
  showInfoBanner?: boolean;
  authTypes?: AuthTypeApi[];
  onProvidersChanged?: () => void;
}
type FormState = CredentialFormFields & {
  clientEndpoint: string;
};
const buildFormState = (stage?: StageConfig): FormState => ({
  endpoint: stage?.endpoint || "",
  clientEndpoint: stage?.endpoint || "",
  authType: stage?.authType || DEFAULT_AUTH_TYPE,
  httpMethod: stage?.httpMethod || "GET",
  requestUrls: stage?.requestUrls || [],
  headers: stage?.headers || [],
  credentialHeaders: stage?.credentialHeaders || [],
  queryParams: stage?.queryParams || [],
  requestBody: stage?.requestBody || [],
  bearerToken: stage?.bearerToken || "",
  username: stage?.username || "",
  password: stage?.password || "",
  headerName: stage?.headerName || "",
  apiKey: stage?.apiKey || "",
});
const StageDtrForm = ({
  provider,
  stage,
  showInfoBanner = true,
  authTypes = [],
  onProvidersChanged,
}: StageDtrFormProps) => {
  const meta = STAGE_META.dtr;
  const description = stage?.description || STAGE_DESCRIPTIONS.dtr;
  const auditUserId = useAuditUserId();
  const [configurationTypes, setConfigurationTypes] = useState<ConfigurationTypeApi[]>([]);
  const [selectedConfigurationTypeId, setSelectedConfigurationTypeId] = useState<
    number | null
  >(null);
  const selectedConfigurationType = useMemo(
    () => configurationTypes.find((t) => t.id === selectedConfigurationTypeId),
    [configurationTypes, selectedConfigurationTypeId],
  );
  const isClientSource = selectedConfigurationType?.configurationType === "client_provided";
  const { loadedStage, setLoadedStage, loadError, isLoading, reloadForAuthType } =
    useStageConfigLoader(
      provider.id,
      "dtr",
      authTypes,
      stage,
      selectedConfigurationTypeId,
    );
  const [form, setForm] = useState<FormState>(() => buildFormState(stage));
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(
    stage?.testResult || null,
  );
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    let cancelled = false;
    ProviderIntegrationApi.getConfigurationTypes("DTR")
      .then((types) => {
        if (cancelled) return;
        setConfigurationTypes(types);
        if (types.length && selectedConfigurationTypeId == null) {
          setSelectedConfigurationTypeId(types[0].id);
        }
      })
      .catch(() => {
        if (!cancelled) setConfigurationTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setForm((prev) => ({
      ...buildFormState(loadedStage),
      authType: loadedStage?.authType || prev.authType,
      clientEndpoint: loadedStage?.endpoint || prev.clientEndpoint,
    }));
    setTestResult(loadedStage?.testResult || null);
  }, [loadedStage]);

  useEffect(() => {
    if (loadError) {
      setTestResult({ message: loadError, status: "error" });
    }
  }, [loadError]);

  const handleQuestionSourceChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextId = Number(event.target.value);
    setSelectedConfigurationTypeId(Number.isFinite(nextId) ? nextId : null);
    setTestResult(null);
  };

  const handleChange =
    (field: keyof FormState) =>
      (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const value = event.target.value;
        setForm((prev) => ({
          ...prev,
          [field]:
            field === "authType"
              ? (value as AuthTypeId)
              : field === "httpMethod"
                ? (value as HttpMethodId)
                : value,
        }));
        setTestResult(null);
        if (field === "authType") {
          const authTypeId = mapAuthTypeIdToApiId(value as AuthTypeId, authTypes);
          if (authTypeId) reloadForAuthType(authTypeId);
        }
      };

  const handleListChange = (
    field: "requestUrls" | "headers" | "credentialHeaders" | "queryParams" | "requestBody",
    items: KeyValueItem[],
  ) => {
    setForm((prev) => ({ ...prev, [field]: items }));
    setTestResult(null);
  };

  const getValidationError = (): string | null => {
    if (!selectedConfigurationTypeId) {
      return "Failed · Missing question source · 0ms";
    }
    if (isClientSource) {
      if (!form.clientEndpoint.trim()) {
        return "Failed · Missing client endpoint · 0ms";
      }
      return null;
    }
    if (!form.endpoint.trim()) {
      return "Failed · Missing MCG API endpoint · 0ms";
    }
    return null;
  };
  const handleSave = async () => {
    const validationError = getValidationError();
    if (validationError) {
      setTestResult({ message: validationError, status: "error" });
      return;
    }
    const providerId = Number(provider.id);
    if (!providerId || !selectedConfigurationTypeId) return;
    setIsSaving(true);
    try {
      const payload = {
        ...form,
        endpoint: isClientSource ? form.clientEndpoint : form.endpoint,
        authType: (isClientSource
          ? AUTH_TYPE.OAuth2ClientCredentials
          : form.authType) as AuthTypeId,
      };
      const result = await saveStageConfig(
        providerId,
        "dtr",
        payload,
        authTypes,
        auditUserId,
        selectedConfigurationTypeId,
      );
      setTestResult({
        message: formatTestResultMessage(result),
        status: isTestSuccess(result.testResult?.testStatus)
          ? "success"
          : "error",
      });
      setLoadedStage({
        ...mapIntegrationToStageConfig(result),
        configurationId: result.id,
      });
      onProvidersChanged?.();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed · Save failed · 0ms";
      setTestResult({
        message: message.startsWith("Failed")
          ? message
          : `Failed · ${message} · 0ms`,
        status: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const fieldId = (name: string) => `dtr-${name}`;
  const displayStatus = loadedStage?.status || stage?.status || "notSet";
  const isNotSet = displayStatus === "notSet";

  return (
    <div className="ic-stage-form-panel">
      {showInfoBanner && (
        <div className="ic-info-banner" role="note">
          <i className="fh_info_fill" aria-hidden="true"></i>
          <span>
            Configure this vendor questionnaire source, then Test &amp; Save
            Configuration.
          </span>
        </div>
      )}
      <div className="ic-stage-form-shell">
        <div className="ic-stage-form-content">
          <header className="ic-stage-form-header">
            <div className="ic-stage-form-title-row">
              <span
                className="ic-stats-icon"
              >
                <i className={meta.icon}></i>
              </span>
              <div className="ic-stage-form-heading-text">
                <div className="ic-stage-form-title-line">
                  <h2 className="ic-stage-form-title">{meta.label}</h2>
                  <StatusBadge status={displayStatus} />
                </div>
                <p className="ic-stage-form-desc mb-0">{description}</p>
              </div>
            </div>
          </header>
          {isLoading && (
            <div className="ic-inline-message" role="status">
              Loading DTR configuration...
            </div>
          )}
          {isNotSet && !isLoading && (
            <StageOutOfScopeMessage stageLabel={meta.label} />
          )}
          <section className="ic-stage-form-section">
            <h3 className="ic-stage-form-section-title">Question source</h3>
            <div className="ic-stage-form-field">
              <label className="ic-stage-form-label" htmlFor={fieldId("source")}>
                Question source
              </label>
              <select
                id={fieldId("source")}
                className="form-select form-select-sm ic-stage-form-input ic-stage-form-select"
                value={selectedConfigurationTypeId ?? ""}
                onChange={handleQuestionSourceChange}
                disabled={!configurationTypes.length}
              >
                {configurationTypes.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.displayName}
                  </option>
                ))}
              </select>
            </div>
          </section>
          {isClientSource ? (
            <section className="ic-stage-form-section">
              <h3 className="ic-stage-form-section-title">Request URL</h3>
              <div className="ic-stage-form-field">
                <label
                  className="ic-stage-form-label"
                  htmlFor={fieldId("client-endpoint")}
                >
                  Client endpoint
                </label>
                <input
                  id={fieldId("client-endpoint")}
                  type="url"
                  className="form-control form-control-sm ic-stage-form-input"
                  value={form.clientEndpoint}
                  onChange={handleChange("clientEndpoint")}
                  placeholder="https://..."
                />
              </div>
            </section>
          ) : (
            <IntegrationCredentialSections
              form={form}
              fieldId={fieldId}
              onFieldChange={handleChange}
              onListChange={handleListChange}
              connectionActive={
                loadedStage?.configurationId != null
                  ? !Boolean(loadedStage.isDeleted)
                  : undefined
              }
            />
          )}
          <footer className="ic-stage-form-footer">
            <button
              type="button"
              className="btn btn-primary btn-sm ic-test-connection-btn"
              onClick={handleSave}
              disabled={isSaving || isLoading || !selectedConfigurationTypeId}
            >
              {isSaving ? (
                <>
                  <span className="ic-test-spinner" aria-hidden="true" />
                  Testing &amp; saving...
                </>
              ) : (
                "Test & Save Configuration"
              )}
            </button>
            {testResult ? (
              <div
                className={`ic-test-result ic-test-result-${testResult.status}`}
                role="status"
              >
                <i
                  className={
                    testResult.status === "error"
                      ? "fh_error_line"
                      : "fh_check_circle_line"
                  }
                  aria-hidden="true"
                ></i>
                <span>{testResult.message}</span>
              </div>
            ) : null}
          </footer>
        </div>
      </div>
    </div>
  );
};

export default StageDtrForm;